import { Badge, CloseButton, Flex, FormatByte, IconButton, Link, Progress } from '@chakra-ui/react'
import { DialogComponent } from '@src/components/DialogComponent/DialogComponent'
import { FileStatus } from '@src/components/FilePreview/types'
import { FileItem } from '@src/components/FilePreviewList/FileItem'
import { progressValueByStatus, colorByStatus, labelByStatus } from '@src/constant'
import { prepareBarChartData } from '@src/helpers/chart'
import { roundNumber } from '@src/helpers/utils'
import { lang } from '@src/lang'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { LuFile, LuRotateCcw } from 'react-icons/lu'

interface Props {
    file: FileItem
    onRemove?: () => void
    onRepeat?: () => void
}

const textHoverStyles = { textDecoration: 'underline', cursor: 'pointer' }

export const FilePreview = observer((props: Props) => {
    const [open, setOpen] = useState(false)

    const progressValue = progressValueByStatus[props.file.status]
    const color = colorByStatus[props.file.status]
    const label = labelByStatus[props.file.status]

    const isDialogAvailable =
        props.file.status === FileStatus.COMPLETED || props.file.status === FileStatus.ERROR

    const iconNonHoverStyles = { backgroundColor: `${color}.subtle`, cursor: 'default' }

    const getData = () => {
        let data = [
            {
                label: lang.status,
                value: <Badge colorPalette={color}>{label}</Badge>,
            },
        ]

        if (props.file.status === FileStatus.COMPLETED) {
            data.push({
                label: lang.size,
                value: <FormatByte value={props.file.size} />,
            })
        }

        if (props.file.status === FileStatus.ERROR) {
            data.push({
                label: lang.error,
                value: <Badge colorPalette={'red'}>{props.file.error?.message}</Badge>,
            })
        }

        return data
    }

    const getTableItems = () => {
        if (props.file.status === FileStatus.COMPLETED) {
            const result = props.file.result
            if (result) {
                const { volumeData, areaData, massData } = prepareBarChartData(result.objects)

                const volumes = volumeData.map(item => ({
                    label: item.type,
                    value: roundNumber(item.value),
                }))

                const masses = areaData.map(item => ({
                    label: item.type,
                    value: roundNumber(item.value),
                }))

                const areas = massData.map(item => ({
                    label: item.type,
                    value: roundNumber(item.value),
                }))

                const items = volumes.map((value, index) => ({
                    id: index + 1,
                    name: value.label,
                    volume: parseFloat(value.value),
                    mass: parseFloat(masses[index].value),
                    area: parseFloat(areas[index].value),
                }))
                return items
            }
        }
    }

    const openDialog = () => {
        if (!isDialogAvailable) {
            return
        }
        setOpen(!open)
    }

    return (
        <Flex padding="2" rounded="sm" borderWidth="1px" alignItems={'center'}>
            <Flex align={'center'} justify={'space-between'} gap={4} flex={1}>
                <IconButton
                    variant={'subtle'}
                    size={'xl'}
                    colorPalette={color}
                    onClick={openDialog}
                    _hover={!isDialogAvailable ? iconNonHoverStyles : {}}
                >
                    <LuFile />
                </IconButton>

                <Progress.Root
                    flex={1}
                    variant={'subtle'}
                    colorPalette={color}
                    value={progressValue}
                >
                    <Flex align={'center'} justify={'space-between'} gap={3} mb={2}>
                        <Progress.Label
                            lineClamp={1}
                            title={props.file.name}
                            onClick={openDialog}
                            _hover={isDialogAvailable ? textHoverStyles : {}}
                        >
                            {props.file.name}
                        </Progress.Label>
                        <Progress.ValueText color={'fg.muted'}>{label}</Progress.ValueText>
                    </Flex>
                    <Progress.Track>
                        <Progress.Range />
                    </Progress.Track>
                </Progress.Root>

                <Flex align={'center'} gap={1}>
                    {(props.file.status === FileStatus.CANCELED ||
                        props.file.status === FileStatus.ERROR) && (
                        <IconButton variant={'ghost'} onClick={props.onRepeat}>
                            <LuRotateCcw />
                        </IconButton>
                    )}
                    <CloseButton colorPalette={'red'} onClick={props.onRemove} />
                </Flex>
            </Flex>
            <DialogComponent
                open={open}
                setOpen={value => setOpen(value)}
                title={props.file.name}
                data={getData()}
                showTable={true}
                tableItems={getTableItems()}
            ></DialogComponent>
        </Flex>
    )
})
